import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { errorMessage } from "../lib/errors";
import { toast } from "sonner";
import { ShieldCheck, Lock, Sparkles, ArrowRight, ExternalLink } from "lucide-react";

export default function Payment() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [cert, setCert] = useState(null);
  const [preview, setPreview] = useState(null); // { amount_euros, discount_applied, original_amount }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/certifications/${slug}`);
        setCert(r.data);
        // fetch current user to compute displayed discount
        const me = await api.get("/user/me").catch(() => ({ data: null }));
        const credit = me?.data?.credit_euros ? Number(me.data.credit_euros) : 0;
        const amount = Number(r.data.price_euros);
        const discount = Math.min(credit, amount);
        setPreview({
          amount_euros: Math.round((amount - discount) * 100) / 100,
          original_amount: amount,
          discount_applied: discount,
        });
      } catch (e) {
        toast.error(errorMessage(e, "Certification introuvable"));
        nav("/categories");
      }
    })();
  }, [slug, nav]);

  const startCheckout = async () => {
    if (!cert || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        certification_id: cert.id,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe Checkout in the same tab
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(errorMessage(e, "Impossible d'initier le paiement"));
      setBusy(false);
    }
  };

  if (!cert || !preview) {
    return <div className="p-10 text-center text-muted2 font-mono text-sm">Préparation du paiement…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="payment-page">
      <div className="text-xs uppercase tracking-widest text-gold-700 font-mono mb-3">Paiement sécurisé</div>
      <h1 className="font-display text-4xl text-ink-900">Finalisez votre achat</h1>
      <p className="text-muted2 mt-2 text-sm">
        Paiement traité en toute sécurité par <strong className="text-ink-900">Stripe</strong>. Aucune donnée de carte
        n'est stockée sur nos serveurs.
      </p>

      <div className="mt-8 grid md:grid-cols-[1fr,300px] gap-6">
        <div className="glass rounded-2xl p-6 space-y-5">
          <div className="flex items-center gap-2 text-xs text-gold-700 uppercase tracking-widest font-mono">
            <Lock className="w-3.5 h-3.5" /> Redirection vers Stripe Checkout
          </div>

          <div className="rounded-xl border border-gold-200 bg-white p-5">
            <div className="text-sm text-ink-700 leading-relaxed">
              Vous allez être redirigé vers <span className="font-medium text-ink-900">Stripe</span> pour saisir vos
              informations de paiement. Une fois le paiement validé, vous reviendrez automatiquement sur E.C.S et votre
              certificat sera émis instantanément.
            </div>
            <ul className="mt-4 text-xs text-muted2 space-y-1.5">
              <li className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-gold-700" /> Cartes bancaires, Apple Pay, Google Pay</li>
              <li className="flex items-center gap-2"><Lock className="w-3.5 h-3.5 text-gold-700" /> Conformité PCI-DSS · 3D Secure</li>
              <li className="flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-gold-700" /> TVA calculée automatiquement</li>
            </ul>
          </div>

          <button
            type="button"
            onClick={startCheckout}
            disabled={busy}
            className="w-full px-5 py-3 rounded-full bg-gold-500 text-ink-900 font-medium hover:bg-gold-400 shadow-gold disabled:opacity-60 inline-flex items-center justify-center gap-2"
            data-testid="stripe-checkout-button"
          >
            {busy ? "Redirection…" : (
              <>Payer {preview.amount_euros.toFixed(2)} € avec Stripe <ExternalLink className="w-4 h-4" /></>
            )}
          </button>

          <p className="text-[11px] text-muted2 text-center">
            En mode <span className="text-gold-700">test</span>, utilisez la carte
            <span className="font-mono mx-1">4242 4242 4242 4242</span>
            (date d'expiration future, CVC quelconque).
          </p>
        </div>

        <aside className="glass rounded-2xl p-6 self-start">
          <h3 className="font-display text-lg text-ink-900">Récapitulatif</h3>
          <div className="gold-divider my-3" />
          <div className="text-sm space-y-2">
            <div className="flex justify-between"><span className="text-muted2">Certification</span><span className="text-ink-900 text-right">{cert.title}</span></div>
            <div className="flex justify-between"><span className="text-muted2">Prix</span><span className="text-ink-900">{preview.original_amount.toFixed(2)}€</span></div>
            {preview.discount_applied > 0 && (
              <div className="flex justify-between text-success"><span>Crédit parrainage</span><span>-{preview.discount_applied.toFixed(2)}€</span></div>
            )}
            <div className="gold-divider my-2" />
            <div className="flex justify-between text-lg">
              <span className="text-ink-900 font-display">Total</span>
              <span className="text-gold-700 font-display" data-testid="payment-total">{preview.amount_euros.toFixed(2)}€</span>
            </div>
          </div>
          <div className="mt-4 text-xs text-muted2 leading-relaxed space-y-2">
            <div className="flex gap-2"><ShieldCheck className="w-3.5 h-3.5 text-gold-700 mt-0.5 flex-shrink-0" />Accès à vie aux leçons et quiz</div>
            <div className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-gold-700 mt-0.5 flex-shrink-0" />Certificat PDF vérifiable</div>
            <div className="flex gap-2"><Lock className="w-3.5 h-3.5 text-gold-700 mt-0.5 flex-shrink-0" />Remboursable 48h si zéro leçon ouverte</div>
          </div>
          <Link to={`/certifications/${slug}`} className="block text-center mt-5 text-xs text-muted2 hover:text-gold-700">← Retour à la fiche</Link>
        </aside>
      </div>
    </div>
  );
}
